<template>
  <section v-if="isRequesting && !isInitialized">
    <spinner-loader :loading="isRequesting" />
  </section>

  <section v-else-if="!isRequesting && !isRequestingUnits && !getUnits.length">
    <p class="mt-5 pt-4">
      You don't have any rental units yet.
      <br />
      Create your first one by clicking "Add Unit."
    </p>
    <b-button @click="onClickAddUnit"> Add Unit </b-button>
  </section>
  <!-- <section v-else-if="errored">
    <p>An error occurred.</p>
  </section> -->
  <section v-else>
    <booking-modal ref="BookingModal" class="booking-modal-btn-new">
      <template slot="submit" />
    </booking-modal>

    <room-rate-modal ref="roomRateSelector" @updated="reopenBookingModal" />

    <div v-if="getBookings">
      <div v-if="'list' == reservationViewType" class="table-container">
        <div class="date-navigation">
          <a class="date-arrow" @click="handlePrevious">
            <i class="fi-play flip-h" />
          </a>

          <button class="today-button" @click="handleToday">Today</button>

          <div class="date-container">
            <b-form-datepicker
              v-model="selectedDate"
              :date-format-options="{
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }"
              class="integrated-picker"
              @input="handleDateChange"
            >
            </b-form-datepicker>
          </div>

          <a class="date-arrow" @click="handleNext">
            <i class="fi-play" />
          </a>
        </div>

        <b-table
          :items="filteredBookings"
          :fields="fields"
          striped
          @row-clicked="rowClicked"
        />
      </div>

      <div v-else>
        <div class="scheduler-sidebar-ghost"></div>
        <scheduler
          ref="scheduler"
          :units.sync="getUnitTypesList"
          :reservations.sync="reservations"
          @renderBookingForm="renderBookingForm"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  data() {
    return {
      reservationViewType: "scheduler",
      isInitialized: false,
      reservations: this.$store.getters.getReservations,
      bookings: this.$store.getters.getBookings,
      filter: "",
      currentDate: this.$moment().format("MMMM Do YYYY"),
      selectedDate: new Date(),
      fields: [
        {
          key: "reservations[0].unit_id",
          label: this.$t("room").toUpperCase(),
          sortable: true,
          thStyle: "border-top:none;",
        },
        {
          key: "name",
          label: this.$t("reservation name").toUpperCase(),
          sortable: true,
          thStyle: "border-top:none;",
        },
        {
          key: "starts_at",
          label: this.$t("check-in date").toUpperCase(),
          sortable: true,
          thStyle: "border-top:none;",
          formatter: (value) => {
            return this.$moment(value).format("MMM D, YYYY");
          },
        },
        {
          key: "ends_at",
          label: this.$t("check-out date").toUpperCase(),
          sortable: true,
          thStyle: "border-top:none;",
          formatter: (value) => {
            return this.$moment(value).format("MMM D, YYYY");
          },
        },
      ],
    };
  },

  components: {
    SpinnerLoader: () => import("@/components/widgets/SpinnerLoader"),
    BookingModal: () => import("@/components/modals/BookingModal"),
    Scheduler: () => import("@/components/Scheduler"),
    RoomRateModal: () => import("@/components/modals/RoomRateModal"),
  },

  computed: {
    ...mapState({
      errored: (state) => state.bookings.errored,
      isRequesting: (state) => state.bookings.isRequesting,
      isRequestingUnits: (state) => state.accommodations.isRequestingUnits,
    }),

    ...mapGetters([
      "getBookings",
      "getBooking",
      "getContact",
      "getUnitTypesList",
      "getReservations",
      "getUnits",
    ]),

    filteredBookings() {
      if (!this.getBookings) return [];

      const selectedDate = this.$moment(this.currentDate, "MMMM Do YYYY");

      return this.getBookings.filter((booking) => {
        const checkIn = this.$moment(booking.starts_at);
        const checkOut = this.$moment(booking.ends_at);

        // Include bookings where the selected date falls between check-in and check-out dates
        return (
          selectedDate.isSameOrAfter(checkIn, "day") &&
          selectedDate.isBefore(checkOut, "day")
        );
      });
    },
  },

  mounted() {
    this.isInitialized = true;
    this.reservations = this.getReservations;
    this.reservationViewType = this.isMobile() ? "list" : "scheduler";
    window.addEventListener("resize", this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },

  methods: {
    handleResize() {
      this.reservationViewType = this.isMobile() ? "list" : "scheduler";
    },
    isMobile() {
      return window.innerWidth <= 768;
    },
    reopenBookingModal(reservation) {
      this.$refs.BookingModal.show(reservation);
    },

    handleShowBookingModal() {
      this.$refs.BookingModal.showEmptyModal();
    },

    handleSaveBooking(formData) {
      this.$refs.BookingModal.saveBooking();
    },

    // List view navigation methods
    handlePrevious() {
      const prevDay = this.$moment(this.currentDate, "MMMM Do YYYY").subtract(
        1,
        "days"
      );
      this.currentDate = prevDay.format("MMMM Do YYYY");
      // Update the datepicker's selected date
      this.selectedDate = prevDay.toDate();
    },

    handleNext() {
      const nextDay = this.$moment(this.currentDate, "MMMM Do YYYY").add(
        1,
        "days"
      );
      this.currentDate = nextDay.format("MMMM Do YYYY");
      // Update the datepicker's selected date
      this.selectedDate = nextDay.toDate();
    },

    rowClicked(booking) {
      // Handle row click to show booking details
      this.$refs.BookingModal.show({
        booking_id: booking.id,
      });
    },

    // Scheduler Events
    renderBookingForm(_event) {
      // on click get available rooms by date and populate rooms
      const dates = {
        starts_at: this.$moment(_event.starts_at).unix(),
        ends_at: this.$moment(_event.ends_at).unix(),
      };

      this.$store.dispatch("fetchAccommodations", dates);
      this.$refs.BookingModal.show(_event);
    },

    onClickAddUnit() {
      this.$router.push({
        path: "/en/settings/accommodations",
      });
    },

    handleDateChange(date) {
      if (date) {
        this.currentDate = this.$moment(date).format("MMMM Do YYYY");
        // If the selectedDate is not a Date object already, convert it
        if (!(this.selectedDate instanceof Date)) {
          this.selectedDate = this.$moment(date).toDate();
        }
      }
    },

    handleToday() {
      const today = this.$moment();
      this.currentDate = today.format("MMMM Do YYYY");
      this.selectedDate = today.toDate();
    },
  },

  watch: {
    isRequesting() {
      if (this.isRequesting) {
        return;
      }

      this.reservations = this.getReservations;

      this.$nextTick(() => {
        if (this.$refs.scheduler) {
          this.$refs.scheduler.update();
        }
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/style/_variables.scss";

.search-header {
  background: $light_grey;
  padding: 10px 0px 10px 0px;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  margin: 0px;
}

.table-container {
  margin: 15px;
  overflow-x: auto;
  background-color: $main_theme_bg_color;
  border-radius: 8px;
  box-shadow: 0 2px 8px $shadow_color;
  height: calc(100vh - 60px);

  @media (max-width: 768px) {
    margin: 10px 5px;

    ::v-deep table {
      font-size: 0.9rem;
    }

    ::v-deep th,
    ::v-deep td {
      padding: 0.75rem;
    }
  }

  ::v-deep table {
    margin-bottom: 0;
    background-color: $snow_white;

    tbody {
      flex: 1 1 auto;
      overflow-y: auto;
    }

    thead th {
      background-color: $light_grey;
      border-bottom: 2px solid $border_color;
      color: $text_dark;
      font-weight: 500;
      padding: 1rem;
    }

    tbody tr {
      transition: all 0.2s;

      &:hover {
        background-color: $soft_blue;
      }

      td {
        color: $text_light;
        border-bottom: 1px solid $border_color;
        vertical-align: middle;
      }
    }
  }
}

.date-navigation {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 0;
  gap: 10px;
}

.date-container {
  display: inline-block;
  width: 250px;
  position: relative;
  height: 40px;
  text-align: center;
}

.integrated-picker {
  ::v-deep .btn {
    background: none;
    border: none;
    padding: 0 15px;
    font-size: 16pt;
    font-weight: 500;

    &:hover {
      color: $text_dark;
      text-decoration: underline;
    }
  }

  ::v-deep .dropdown-menu {
    min-width: 240px;
    margin-top: 10px;
  }

  ::v-deep .b-calendar-nav button {
    padding: 5px;
  }
}

.date-text {
  display: inline-block;
  padding: 0 5px;
}

.date-arrow {
  cursor: pointer;
  color: $primary_blue;
  transition: all 0.2s;
  padding: 0.5rem;
  border-radius: 4px;

  &:hover {
    background-color: $soft_blue;
    color: $primary_blue_hover;
  }

  i {
    font-size: 20pt;
  }
}

.header-left {
  text-align: left;
}

.header-left i {
  font-size: 22pt;
}

.header-right {
  text-align: right;
}

.header-right i {
  font-size: 22pt;
}

.v-switch-label {
  padding-top: 1px;
}

.flip-h:before {
  transform: scale(-1, 1);
}

i {
  font-size: 20pt;
  cursor: pointer;
}

.scheduler-sidebar-ghost {
  width: 200px;
  height: 100%;
  position: absolute;
  background-color: $light_grey;
}

.today-button {
  background: none;
  border: 1px solid $border_color;
  color: $text_light;
  padding: 5px 15px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;

  &:hover {
    background-color: $soft_blue;
    color: $primary_blue;
    border-color: $primary_blue;
  }
}
</style>
